// ----- Phase 5.X-settings-wiring-admin-forms — useSetting hook -----
//
// Per-key reader on top of `v2.settings.listAll()` (and, T-17 D6,
// `v2.settings.getSubmission(id)`). Returns the post-merge value for one
// SETTINGS_SCHEMA key, with the schema's default value as the fallback when no
// row exists at any tier. The merge itself (submission > user > org > default,
// OrgLocked wins, scope filtering by the registry) happens server-side in the
// ONE settings resolver (`src/services/settings/resolve.ts`) — this hook only
// reads its output.
//
// Module-level cache mirrors useUserPermissions (load once, share across
// components). There is one cache entry for the unscoped read (`listAll`) and
// one PER SUBMISSION id (`getSubmission(id)`): a submission-level override must
// not leak into the unscoped entry (and vice versa), and a failed fetch for one
// submission must not poison another. The cache is invalidated by a manual
// `refreshAllSettings()` call (every entry re-fetches) OR when any per-settings
// page write finishes (the settings page already calls it).
//
// Why a hook (vs. an inline `v2.settings.listAll()` call): per-setting wiring
// phases need to read effective values from multiple components without each
// component re-fetching. The hook caches once and broadcasts to all
// subscribers when the data lands.

import { useEffect, useState } from 'react';
import {
  v2,
  ApiError,
  type MergedSettingsMap,
} from '@/api/v2Client';
import { findSchemaEntry } from '@/lib/settings-schema';

type EntryState =
  | { kind: 'idle' }
  | { kind: 'loading'; promise: Promise<void> }
  | { kind: 'loaded'; settings: MergedSettingsMap }
  | { kind: 'error'; at: number };

interface CacheEntry {
  state: EntryState;
}

/** Same cool-off the permissions cache uses (2d2af0a class): a transient
 *  failure must not latch defaults for the whole SPA session. */
export const SETTINGS_ERROR_RETRY_MS = 5_000;

/** The unscoped (org + user) entry's key; submission entries use their id. */
const UNSCOPED = '';

const entries = new Map<string, CacheEntry>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of listeners) fn();
}

function entryFor(scope: string): CacheEntry {
  let e = entries.get(scope);
  if (!e) {
    e = { state: { kind: 'idle' } };
    entries.set(scope, e);
  }
  return e;
}

async function fetchEntry(scope: string): Promise<void> {
  const entry = entryFor(scope);
  try {
    const res =
      scope === UNSCOPED ? await v2.settings.listAll() : await v2.settings.getSubmission(scope);
    entry.state = { kind: 'loaded', settings: res.settings };
  } catch (err) {
    // 401/403/network failure → degrade silently. The caller falls back
    // to the schema's defaultValue (loading=false, value=default).
    if (!(err instanceof ApiError)) {
      console.warn('[useSetting] unexpected fetch error', err);
    }
    entry.state = { kind: 'error', at: Date.now() };
  } finally {
    notify();
  }
}

function ensureLoad(scope: string): void {
  const entry = entryFor(scope);
  const retryableError =
    entry.state.kind === 'error' && Date.now() - entry.state.at >= SETTINGS_ERROR_RETRY_MS;
  if (entry.state.kind === 'idle' || retryableError) {
    entry.state = { kind: 'loading', promise: fetchEntry(scope) };
  }
}

function refreshScope(scope: string): Promise<void> {
  const entry = entryFor(scope);
  const promise = fetchEntry(scope);
  entry.state = { kind: 'loading', promise };
  notify();
  return promise;
}

/** Test-only reset. */
export function __resetSettingsCacheForTests(): void {
  entries.clear();
  listeners.clear();
}

/**
 * Force a re-fetch of every cached entry (the unscoped one always, plus each
 * submission entry already loaded) — call after a settings write or an identity
 * switch. Returns the promise so callers can await the refresh if needed.
 */
export function refreshAllSettings(): Promise<void> {
  const scopes = new Set<string>([UNSCOPED, ...entries.keys()]);
  return Promise.all([...scopes].map((s) => refreshScope(s))).then(() => undefined);
}

export interface UseSettingOptions {
  /** Read the SUBMISSION-merged value (submission > user > org > default). */
  readonly submissionId?: string | null | undefined;
}

export interface UseSettingResult<T extends boolean | number | string> {
  /** True while the first fetch is in-flight. After load, false forever. */
  readonly loading: boolean;
  /** Effective value after merge; falls back to the schema's defaultValue. */
  readonly value: T;
  /** Tier the value came from. 'default' when no row exists. */
  readonly source: 'org' | 'user' | 'submission' | 'default';
  /** True when the org row is OrgLocked (user/submission writes blocked). */
  readonly orgLocked: boolean;
  /** Force a refresh (of THIS hook's entry — unscoped, or its submission). */
  readonly refresh: () => Promise<void>;
}

/**
 * Read one SETTINGS_SCHEMA key's effective value. The generic `T`
 * defaults to the schema entry's defaultValue type at the call site.
 *
 * `useSetting(key)` reads the org + user merge; `useSetting(key, { submissionId })`
 * reads the submission-merged settings, so a submission-level override applies
 * (T-17 D6). Callers that act on a specific submission MUST pass its id.
 *
 * Returns `{loading, value, source, orgLocked, refresh}`. Until the
 * first fetch resolves, `loading=true` and `value` equals the schema
 * default (so the UI never flashes the wrong state).
 *
 * Unknown keys (not in SETTINGS_SCHEMA) throw — by design, this hook is
 * only for schema-known settings. The generic key-value editor's reads
 * go through `v2.settings.listAll()` directly.
 */
export function useSetting<T extends boolean | number | string>(
  key: string,
  options: UseSettingOptions = {},
): UseSettingResult<T> {
  const scope = options.submissionId ? options.submissionId : UNSCOPED;
  const [, force] = useState(0);

  useEffect(() => {
    ensureLoad(scope);
    const tick = (): void => force((n) => n + 1);
    listeners.add(tick);
    return () => {
      listeners.delete(tick);
    };
  }, [scope]);

  const entry = findSchemaEntry(key);
  if (!entry) {
    throw new Error(`useSetting: unknown schema key "${key}"`);
  }
  const defaultValue = entry.defaultValue as T;
  const refresh = (): Promise<void> => refreshScope(scope);

  const state: EntryState = entries.get(scope)?.state ?? { kind: 'idle' };
  const loaded = state.kind === 'loaded' ? state.settings : null;
  const merged = loaded ? loaded[key] : undefined;

  const loading = state.kind === 'idle' || state.kind === 'loading';

  if (!merged) {
    return {
      loading,
      value: defaultValue,
      source: 'default',
      orgLocked: false,
      refresh,
    };
  }
  return {
    loading,
    value: merged.Value as T,
    source: merged.Source,
    orgLocked: merged.OrgLocked,
    refresh,
  };
}
